import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { useToast } from '../components/ui/Toast';
import { parseApiError } from '../utils/apiError';

// Change password while already logged in — proof of identity is the current
// password, not an emailed link, so this skips the reset-token flow entirely.
export default function ChangePassword() {
  const { changePassword } = useAuth();
  const { addToast } = useToast();

  const [values, setValues] = useState({ currentPassword: '', password: '', password2: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const setField = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      await changePassword(values.currentPassword, values.password, values.password2);
      setValues({ currentPassword: '', password: '', password2: '' });
      addToast('Password changed', { variant: 'success' });
    } catch (err) {
      const { formError: fe, fieldErrors: fld } = parseApiError(err);
      setFormError(fe);
      setFieldErrors(fld);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="ss-container" style={{ paddingBlock: 'var(--ss-space-6)' }}>
      <p style={{ marginBottom: 'var(--ss-space-3)' }}>
        <Link to="/profile" className="ss-back-link"><ArrowLeft size={16} aria-hidden="true" /> Back to profile</Link>
      </p>
      <h1>Change password</h1>

      <div style={{ marginTop: 'var(--ss-space-5)', maxWidth: '32rem' }}>
        <Card>
          <form className="ss-form" onSubmit={submit} noValidate>
            {formError && <p className="ss-form__error" role="alert">{formError}</p>}

            <Input
              label="Current password"
              type="password"
              value={values.currentPassword}
              onChange={setField('currentPassword')}
              error={fieldErrors.currentPassword}
              required
              autoFocus
              autoComplete="current-password"
            />
            <Input
              label="New password"
              type="password"
              value={values.password}
              onChange={setField('password')}
              error={fieldErrors.password}
              required
              autoComplete="new-password"
            />
            <Input
              label="Confirm new password"
              type="password"
              value={values.password2}
              onChange={setField('password2')}
              error={fieldErrors.password2}
              required
              autoComplete="new-password"
            />

            <div className="ss-form__actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? <Spinner label="Saving" /> : 'Change password'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
}
